import { useState, useCallback, useRef, useMemo } from 'react';
import api from '../utils/api';

export function useGroupStore() {
  const [groups, setGroups] = useState([]);
  const groupsRef = useRef(groups);
  const searchRequestRef = useRef(0);
  groupsRef.current = groups;

  // Insert or update a group in state
  const upsertGroup = useCallback((updatedGroup) => {
    setGroups((prev) => {
      const exists = prev.some((g) => g._id === updatedGroup._id);
      if (exists) {
        return prev.map((g) => (g._id === updatedGroup._id ? updatedGroup : g));
      } else {
        return [updatedGroup, ...prev];
      }
    });
  }, []);

  // Remove a group by ID
  const removeGroup = useCallback((groupId) => {
    setGroups((prev) => prev.filter((g) => g._id !== groupId));
  }, []);

  // Get a group synchronously from current state by ID
  const getGroup = useCallback((groupId) => {
    return groupsRef.current.find((g) => g._id === groupId) || null;
  }, []);

  // Fetch single group from API and upsert into state
  const fetchGroup = useCallback(
    async (groupId) => {
      if (!groupId) return null;
      try {
        const res = await api.get(`/api/groups/${groupId}`);
        const group = res.data?.group || res.data;
        if (group) {
          upsertGroup(group);
        }
        return group;
      } catch (err) {
        console.warn(`Failed to fetch group ${groupId}`, err);
        return null;
      }
    },
    [upsertGroup]
  );

  // Fetch multiple groups from API with optional query params (e.g. supplierId)
  const fetchGroups = useCallback(
    async (params = {}) => {
      const requestId = ++searchRequestRef.current;
      try {
        const res = await api.get('/api/groups/search', { params });
        const fetched = res.data;
        // Support both direct array or { groups: [...] } response shapes
        const list = Array.isArray(fetched)
          ? fetched
          : Array.isArray(fetched.groups)
          ? fetched.groups
          : [];
        // Ignore responses from a search that a newer one has already superseded
        if (requestId === searchRequestRef.current) setGroups(list);
        return list; // Return list for caller usage if needed
      } catch (err) {
        console.error('Failed to fetch groups', err);
        throw err;
      }
    },
    []
  );

  // Groups anyone can browse, without logging in (no member contact details)
  const fetchPublicGroups = useCallback(async () => {
    const res = await api.get('/api/groups/public');
    const list = Array.isArray(res.data) ? res.data : [];
    setGroups(list);
    return list;
  }, []);

  // Alias for upsertGroup: replace an existing group (or insert it if missing)
  const replaceGroup = upsertGroup;

  return useMemo(
    () => ({
      groups,
      fetchGroups,
      fetchPublicGroups,
      fetchGroup,
      upsertGroup,
      replaceGroup,
      removeGroup,
      getGroup,
    }),
    [groups, fetchGroups, fetchPublicGroups, fetchGroup, upsertGroup, replaceGroup, removeGroup, getGroup]
  );
}
